import { AxiosInstance, AxiosResponse } from 'axios';
import { config } from 'config';
import { SERVICE_DOWN } from 'api/errors';
import * as Sentry from '@sentry/react';
import { User, Tokens, Address } from 'types';
import { AxiosClient } from '../AxiosClient/AxiosClient';
import { catchHandler, setTokens } from './PromiseHandlers';
import { TargetEmail } from '@groupelacentrale/classified-core/model/Contacts';

class UserManagement {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = AxiosClient.get({
      baseURL: config.URL_USER_MANAGEMENT,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async createUser(email: string, password: string): Promise<Tokens> {
    return this.client
      .post<Tokens>('v1/users', {
        email,
        newPassword: password,
        site: config.USER_MANAGEMENT_SITE,
        termsAndConditionsValidationDate: new Date(),
      })
      .then(setTokens)
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'UM',
            endpoint: 'v1/users',
          },
        });
        if (r.response) {
          const response: AxiosResponse<any> = r.response;
          if (response.status === 400) {
            return Promise.reject(response.data.message.fields);
          } else {
            return Promise.reject(response.data.message);
          }
        }
        return Promise.reject(SERVICE_DOWN);
      });
  }

  async resetPassword(email: string): Promise<AxiosResponse> {
    return this.client
      .post('v1/password/reset', {
        username: email,
        site: config.USER_MANAGEMENT_SITE,
        idTemplate: config.TEMPLATE_RESET_PASSWORD,
        urlUpdatePassword: config.URL_RESET_PASSWORD,
      })
      .then()
      .catch(r => catchHandler(r, 'password/reset'));
  }

  async getUser(id: string): Promise<User> {
    return this.client
      .get<User>(`/v1/users/${id}`, {
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(r => {
        const user: User = r.data;
        const addresses =
          user.addresses &&
          user.addresses.reduce<Address[]>((addresses, address) => {
            return [...addresses, { ...address, zipCode: String(address.zipCode) }];
          }, []);
        return { ...user, addresses };
      })
      .catch(r => catchHandler(r, 'users'));
  }

  async updateUser(id: string, userData: Partial<User>): Promise<Tokens> {
    return this.client
      .patch<Tokens>(
        `/v1/users/${id}`,
        { ...userData, id },
        {
          headers: AxiosClient.getAuthorizationHeader(),
        },
      )
      .then(setTokens)
      .catch(r => catchHandler(r, 'users'));
  }

  async getUserEmailingData(id: string): Promise<TargetEmail | undefined> {
    return this.getUser(id).then(user => {
      return user.targetEmail;
    });
  }
}

export const UserManagementService = new UserManagement();
